import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { HelpHint } from '../components/HelpHint';
import { api } from '../lib/api';
import { ScrollX } from '../components/ScrollX';

/**
 * Who did what, and whether any of it looks wrong.
 *
 * A flat list of events answers the first question and not the second, which is
 * the one an admin actually opens this page for. So it leads with the signals —
 * failed sign-ins, credential changes, addresses that look like they are
 * guessing — and the list underneath is for following up on them.
 *
 * The rows come from a table the database refuses to update or delete. That is
 * what separates an audit trail from a log file, and it is why nothing on this
 * page can change anything.
 */

/** Actions worth naming in plain words. Anything else shows its raw code. */
const ACTIONS: Record<string, { label: string; tone?: 'security' | 'change' }> = {
  'auth.login': { label: 'Signed in' },
  'auth.login_failed': { label: 'Failed sign-in', tone: 'security' },
  'auth.logout': { label: 'Signed out' },
  'auth.password_changed': { label: 'Changed their own password', tone: 'security' },
  'user.created': { label: 'Added a user', tone: 'security' },
  'user.updated': { label: 'Changed a user', tone: 'security' },
  'user.password_reset': { label: 'Reset someone’s password', tone: 'security' },
  'settings.updated': { label: 'Changed settings', tone: 'change' },
  'technician.created': { label: 'Added a technician', tone: 'change' },
  'technician.updated': { label: 'Changed a technician', tone: 'change' },
  'technician.deactivated': { label: 'Took a technician off shift', tone: 'change' },
  'ticket.archived': { label: 'Archived a ticket', tone: 'change' },
  'ticket.linked_to_incident': { label: 'Linked a ticket to an incident' },
  'ticket.marked_incident': { label: 'Marked a ticket as an incident' },
  'ticket.unmarked_incident': { label: 'Unmarked an incident' },
  'incident.resolved': { label: 'Resolved an incident' },
  'message.replayed': { label: 'Resent a message' },
};

const FILTERS = [
  { id: '', label: 'Everything' },
  { id: 'auth', label: 'Sign-ins' },
  { id: 'user', label: 'Accounts' },
  { id: 'settings', label: 'Settings' },
  { id: 'technician', label: 'Technicians' },
  { id: 'ticket', label: 'Tickets' },
];

export function AuditPage() {
  const [action, setAction] = useState('');
  const [actorId, setActorId] = useState('');
  const [hours, setHours] = useState(168);

  const { data: signals } = useQuery({
    queryKey: ['audit-signals'],
    queryFn: () => api.auditSignals(24),
    refetchInterval: 120_000,
  });
  const { data: actors } = useQuery({ queryKey: ['audit-actors'], queryFn: api.auditActors });
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['audit', action, actorId, hours],
    queryFn: () => api.audit({ action, actorId, hours, limit: 250 }),
  });

  return (
    <div className="space-y-5 pb-10">
      <div>
        <h2 className="flex items-center gap-2 font-heading text-2xl">
          <ShieldCheck className="h-5 w-5 text-primary-600" aria-hidden="true" />
          Audit log
          <HelpHint>
            Every administrative action, in order. The database refuses to change or delete these
            rows — not even an admin can, which is what makes them evidence rather than notes.
          </HelpHint>
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          What changed, who changed it, and where they were.
        </p>
      </div>

      {/* ── Does anything need attention ─────────────────────────────────── */}
      {signals && (
        <section className="card">
          <h3 className="mb-3 flex items-center gap-1.5 font-heading text-lg">
            Last 24 hours
            <HelpHint>
              The counts an admin should glance at daily. Failed sign-ins clustered on one address
              are what password guessing looks like.
            </HelpHint>
          </h3>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Signal label="Sign-ins" value={signals.signIns} />
            <Signal
              label="Failed sign-ins"
              value={signals.failedSignIns}
              tone={signals.failedSignIns > 10 ? 'warning' : 'calm'}
            />
            <Signal
              label="Account changes"
              value={signals.credentialChanges}
              hint="Users added, passwords reset or changed. Should match what you know you did."
            />
            <Signal
              label="Settings changes"
              value={signals.configChanges}
              hint="Includes any change to the messaging accounts."
            />
          </div>

          {signals.suspiciousAddresses.length > 0 && (
            <div className="mt-4 rounded-xl border border-danger-200 bg-danger-50/50 p-4">
              <p className="flex items-center gap-2 font-medium text-danger-800">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                {signals.suspiciousAddresses.length} address
                {signals.suspiciousAddresses.length === 1 ? '' : 'es'} worth looking at
              </p>
              <ul className="mt-2 space-y-1 text-sm text-danger-900">
                {signals.suspiciousAddresses.map((a) => (
                  <li key={a.ip} className="font-mono text-xs">
                    {a.ip} — {a.attempts} failed attempt{a.attempts === 1 ? '' : 's'}
                    {a.accountsTried > 1 && ` across ${a.accountsTried} accounts`}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-danger-800">
                Sign-in is rate-limited per account and per address, so this is attempts made, not
                attempts that got anywhere. If you do not recognise an address, change the passwords
                on the accounts named below.
              </p>
            </div>
          )}

          {signals.failedSignIns === 0 && signals.suspiciousAddresses.length === 0 && (
            <p className="mt-3 text-sm text-muted-foreground">
              No failed sign-ins in the last 24 hours.
            </p>
          )}
        </section>
      )}

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setAction(f.id)}
              className={`rounded-lg border px-3 py-1.5 text-sm ${
                action === f.id
                  ? 'border-primary-500 bg-primary-50 text-primary-800'
                  : 'border-border text-muted-foreground hover:border-muted-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Who</span>
          <select
            className="input"
            value={actorId}
            onChange={(e) => setActorId(e.target.value)}
          >
            <option value="">Anyone</option>
            {(actors?.actors ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Since</span>
          <select className="input" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            <option value={24}>Last 24 hours</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </select>
        </label>
      </div>

      {/* ── The trail ────────────────────────────────────────────────────── */}
      {isPending && <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-primary-500" />}
      {isError && <p className="text-danger-600">{(error as Error).message}</p>}

      {data && (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          <ScrollX>
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/40 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">When</th>
                  <th className="px-4 py-3 font-semibold">Who</th>
                  <th className="px-4 py-3 font-semibold">Did what</th>
                  <th className="px-4 py-3 font-semibold">
                    <span className="inline-flex items-center gap-1.5">
                      What changed
                      <HelpHint>
                        The fields that differed. Passwords and API keys show as redacted — that a
                        credential changed is the fact worth recording; its value is not.
                      </HelpHint>
                    </span>
                  </th>
                  <th className="px-4 py-3 font-semibold">From</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.events.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                      Nothing matches those filters.
                    </td>
                  </tr>
                )}
                {data.events.map((event) => {
                  const known = ACTIONS[event.action];
                  return (
                    <tr
                      key={event.id}
                      className={known?.tone === 'security' ? 'bg-accent-50/30' : ''}
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                        {new Date(event.occurredAt).toLocaleString('en-KE', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="font-medium">{event.actor}</span>
                        {event.actorEmail && event.actorEmail !== event.actor && (
                          <span className="block text-xs text-muted-foreground">
                            {event.actorEmail}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {known?.label ?? event.action}
                        {known?.tone === 'security' && (
                          <span className="ml-2 badge bg-accent-50 text-accent-700">security</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {event.changes.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <ul className="space-y-0.5 text-xs">
                            {event.changes.slice(0, 4).map((c) => (
                              <li key={c.field}>
                                <span className="font-medium">{c.field}</span>{' '}
                                <span className="text-muted-foreground">
                                  {format(c.from)} → {format(c.to)}
                                </span>
                              </li>
                            ))}
                            {event.changes.length > 4 && (
                              <li className="text-muted-foreground">
                                and {event.changes.length - 4} more
                              </li>
                            )}
                          </ul>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-muted-foreground">
                        {event.ip ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollX>
        </div>
      )}
    </div>
  );
}

function Signal({
  label,
  value,
  hint,
  tone = 'calm',
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: 'calm' | 'warning';
}) {
  return (
    <div className={`rounded-xl border p-3 ${tone === 'warning' ? 'border-accent-300 bg-accent-50/40' : 'border-border'}`}>
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        {label}
        {hint && <HelpHint>{hint}</HelpHint>}
      </div>
      <div
        className={`mt-0.5 text-2xl font-semibold tabular-nums ${tone === 'warning' ? 'text-accent-700' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}

/** Short, readable values. A whole object in a table cell is unreadable. */
function format(value: unknown): string {
  if (value === null || value === undefined) return 'not set';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'none';
  if (typeof value === 'object') return '(details)';
  const text = String(value);
  return text.length > 40 ? `${text.slice(0, 40)}…` : text;
}
